import DOMPurify from "dompurify";

/* Interactive / structural chrome carried over from the scraped Framer pages.
   None of it is article content — it only renders stray input boxes, nav bars
   and empty layout wrappers, so it is stripped entirely. */
const FORBID_TAGS = [
  "form", "input", "button", "textarea", "select", "option", "label",
  "fieldset", "legend", "nav", "header", "footer", "aside",
  "style", "link", "script", "svg", "iframe", "object", "embed", "noscript"
];

/* Article HTML body -> sanitized, de-Framered HTML for inline rendering.
   Only used for Directus article bodies (see ResolvedPage). The scraped bodies
   are full Framer page dumps: nested layout <div>s with framer-* classes,
   inline styles, data-* attributes and a duplicate title/category header. We
   drop all presentation so the body inherits the site's own `.rich-text`
   styles instead of Framer's leftover chrome. */
export function renderHtml(input, { title } = {}) {
  if (!input) return "";
  const clean = DOMPurify.sanitize(String(input), {
    FORBID_TAGS,
    // Strip presentation + identity attributes so nothing styles itself.
    // srcset/sizes carry broken relative Framer paths; dropping them forces the
    // browser to use the absolute Supabase `src` instead.
    FORBID_ATTR: ["style", "class", "id", "srcset", "sizes"],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ["loading"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i
  });
  return tidy(clean, title);
}

const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();

const HEADINGS_AND_TEXT = "h1, h2, h3, h4, h5, h6, p";

/* Headings/eyebrows that mark the start of the Framer page footer appended to
   every scraped body (a "Latest articles" related-posts grid and a "Subscribe"
   newsletter CTA). Everything from the first such marker onward is page chrome,
   not article content. */
const TRAILING_MARKERS = [
  "latest articles",
  "subscribe for our latest update.",
  "subscribe for our latest update",
  "stay in the loop"
];

/* Remove `el` and everything that follows it in document order, keeping all
   content before it (ancestors are preserved, only their trailing content is
   stripped). */
function removeFrom(el) {
  let node = el;
  while (node && node.parentNode && node.tagName !== "BODY") {
    while (node.nextSibling) node.nextSibling.remove();
    node = node.parentNode;
  }
  el.remove();
}

/* Post-pass on the sanitized markup. The scraped bodies wrap the real article
   in Framer page chrome: a leading header (Category / duplicate title /
   description / date) before the banner image, and a trailing related-posts +
   newsletter footer. We strip both so only the article itself renders under the
   site's own `.rich-text` styles. */
function tidy(html, title) {
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return html;
  }
  const doc = new window.DOMParser().parseFromString(html, "text/html");

  // Trailing: cut from the first page-footer marker onward.
  const marker = [...doc.body.querySelectorAll(HEADINGS_AND_TEXT)].find((el) =>
    TRAILING_MARKERS.includes(norm(el.textContent))
  );
  if (marker) removeFrom(marker);

  // Leading: the header stub (Category/title/description/date) always precedes
  // the lead banner image, so drop any heading/paragraph that sits before the
  // first image. (DOCUMENT_POSITION_FOLLOWING = 4, CONTAINED_BY = 16 — the mask
  // keeps elements that come before the image without containing it.)
  const firstImg = doc.body.querySelector("img");
  if (firstImg) {
    doc.body.querySelectorAll(HEADINGS_AND_TEXT).forEach((el) => {
      const pos = el.compareDocumentPosition(firstImg);
      if (pos & 4 && !(pos & 16)) el.remove();
    });
  } else if (title) {
    // No banner to anchor on — fall back to removing a heading that merely
    // repeats the page title.
    const wanted = norm(title);
    const dup = [...doc.body.querySelectorAll("h1, h2")].find(
      (h) => norm(h.textContent) === wanted
    );
    if (dup) dup.remove();
  }

  // Drop empty headings/paragraphs left behind by stripped Framer bindings.
  doc.body.querySelectorAll(HEADINGS_AND_TEXT).forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector("img, video, picture")) {
      el.remove();
    }
  });

  // Drop wrappers that hold no text and no media (outer-to-inner document order
  // means removing an empty parent takes its empty children with it).
  doc.body.querySelectorAll("div, span, section, article").forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector("img, video, picture")) {
      el.remove();
    }
  });

  return doc.body.innerHTML;
}
